import React, { useRef, useEffect, useState, useCallback } from "react";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { FaPlay, FaPause, FaRedo, FaTrashAlt } from "react-icons/fa";

gsap.registerPlugin(Draggable);

function drawStaticWaveform(canvas, peaks, progress, isOwn) {
  if (!canvas || !peaks || peaks.length === 0) return;
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  const barCount = peaks.length;
  const barWidth = Math.max((w / barCount) - 1, 2);
  const gap = 1;

  for (let i = 0; i < barCount; i++) {
    const val = peaks[i] || 0;
    const barH = Math.max(val * h * 0.85, 2);
    const x = i * (barWidth + gap);
    const y = (h - barH) / 2;
    const barProgress = i / barCount;

    if (progress > 0 && barProgress <= progress) {
      ctx.fillStyle = "#00a884";
    } else {
      ctx.fillStyle = "rgba(0, 168, 132, 0.25)";
    }

    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, barWidth, barH, 1);
    } else {
      ctx.rect(x, y, barWidth, barH);
    }
    ctx.fill();
  }
}

export default function PreviewAudio({ blob, duration, peaks, onSend, onCancel, isSending }) {
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const audioBufferRef = useRef(null);
  const startTimeRef = useRef(0);
  const offsetRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [audioReady, setAudioReady] = useState(false);
  const animRef = useRef(null);

  const sendTrackRef = useRef(null);
  const sendKnobRef = useRef(null);

  useEffect(() => {
    if (!blob || !peaks || peaks.length === 0) return;
    setAudioReady(false);

    drawStaticWaveform(canvasRef.current, peaks, 0, false);

    blob.arrayBuffer().then((buf) => {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctx.decodeAudioData(buf).then((audioBuffer) => {
        audioBufferRef.current = audioBuffer;
        setAudioReady(true);
        ctx.close();
      }).catch(() => { ctx.close(); });
    });

    return () => {
      setAudioReady(false);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (sourceRef.current) { try { sourceRef.current.stop(); } catch (e) {} }
      if (audioCtxRef.current) { audioCtxRef.current.close(); }
    };
  }, [blob, peaks]);

  useEffect(() => {
    if (!sendKnobRef.current || isSending) return;
    const el = sendKnobRef.current;
    const track = sendTrackRef.current;

    const drag = Draggable.create(el, {
      type: "x",
      bounds: { minX: 0, maxX: 150 },
      edgeResistance: 0.8,
      onDrag() {
        const p = this.x / 150;
        gsap.to(track, {
          backgroundColor: "rgba(0, 92, 75, " + (0.08 + p * 0.92) + ")",
          duration: 0.05,
        });
      },
      onDragEnd() {
        if (this.x >= 100) {
          onSend();
          gsap.to(el, { x: 0, duration: 0.3, ease: "back.out(1.7)" });
          gsap.to(track, { backgroundColor: "var(--border, #e4e6eb)", duration: 0.3 });
        } else {
          gsap.to(el, { x: 0, duration: 0.4, ease: "elastic.out(1, 0.3)" });
          gsap.to(track, { backgroundColor: "var(--border, #e4e6eb)", duration: 0.3 });
        }
      },
    })[0];

    return () => drag.kill();
  }, [onSend, isSending]);

  const tick = useCallback(() => {
    if (!audioCtxRef.current || !duration) return;
    const elapsed = (audioCtxRef.current.currentTime - startTimeRef.current) * speed;
    const total = duration - offsetRef.current;
    const p = Math.min(elapsed / total, 1);
    const fullProgress = offsetRef.current / duration + p * (1 - offsetRef.current / duration);
    setProgress(fullProgress);
    drawStaticWaveform(canvasRef.current, peaks, fullProgress, false);
    if (elapsed < total) {
      animRef.current = requestAnimationFrame(tick);
    } else {
      setIsPlaying(false);
      setProgress(0);
      offsetRef.current = 0;
      drawStaticWaveform(canvasRef.current, peaks, 0, false);
    }
  }, [duration, speed, peaks]);

  const startPlayback = (fromOffset) => {
    if (!audioBufferRef.current) return;

    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    const ctx = audioCtxRef.current;

    const source = ctx.createBufferSource();
    source.buffer = audioBufferRef.current;
    source.playbackRate.value = speed;
    source.connect(ctx.destination);
    sourceRef.current = source;

    source.start(0, fromOffset);
    startTimeRef.current = ctx.currentTime;
    offsetRef.current = fromOffset;
    setIsPlaying(true);
    animRef.current = requestAnimationFrame(tick);

    source.onended = () => {
      if (offsetRef.current > 0 || isPlaying) {
        offsetRef.current = 0;
        setProgress(0);
        setIsPlaying(false);
        drawStaticWaveform(canvasRef.current, peaks, 0, false);
      }
    };
  };

  const stopPlayback = () => {
    if (sourceRef.current) { try { sourceRef.current.stop(); } catch (e) {} }
    if (animRef.current) cancelAnimationFrame(animRef.current);
  };

  const togglePlay = () => {
    if (!audioBufferRef.current) return;

    if (isPlaying) {
      const ctx = audioCtxRef.current;
      offsetRef.current += (ctx.currentTime - startTimeRef.current) * speed;
      stopPlayback();
      setIsPlaying(false);
      return;
    }

    startPlayback(offsetRef.current);
  };

  const cycleSpeed = (e) => {
    e.stopPropagation();
    const speeds = [1, 1.5, 2];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length];
    setSpeed(next);
    if (isPlaying) {
      const ctx = audioCtxRef.current;
      const elapsed = (ctx.currentTime - startTimeRef.current) * speed;
      offsetRef.current += elapsed;
      stopPlayback();
      startPlayback(offsetRef.current);
    }
  };

  const handleReplay = (e) => {
    e.stopPropagation();
    stopPlayback();
    offsetRef.current = 0;
    setProgress(0);
    setIsPlaying(false);
    drawStaticWaveform(canvasRef.current, peaks, 0, false);
    startPlayback(0);
  };

  const fmt = (sec) => {
    const s = Math.floor(sec || 0);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m + ":" + r.toString().padStart(2, "0");
  };

  return (
    <div className="preview-container">
      <div className="preview-waveform">
        <button
          className="preview-play-btn"
          onClick={togglePlay}
          disabled={isSending || !audioReady}
        >
          {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} style={{ marginLeft: 2 }} />}
        </button>
        <div className="preview-waveform-canvas">
          <canvas ref={canvasRef} width={400} height={36} style={{ width: "100%", height: "100%" }} />
        </div>
        <span className="preview-duration">{fmt(isPlaying ? progress * duration : duration)}</span>
      </div>

      <div className="preview-controls">
        <button className="preview-control-btn" onClick={handleReplay} disabled={isSending || !audioReady} title="Rejouer">
          <FaRedo size={12} />
        </button>
        <button className="preview-control-btn speed" onClick={cycleSpeed} disabled={isSending || !audioReady} title={"Vitesse: " + speed + "x"}>
          {speed}x
        </button>
      </div>

      <div className="preview-actions-row">
        <button className="preview-delete-btn" onClick={onCancel} disabled={isSending} title="Supprimer l'enregistrement">
          <FaTrashAlt size={14} />
        </button>
        <div className="send-track" ref={sendTrackRef}>
          <span className="send-track-label">Glissez pour envoyer</span>
          <div className="send-knob" ref={sendKnobRef}>
            <svg viewBox="0 0 24 24">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
